import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminStats, adminListAnalyses, adminListLeads, adminGetDailyLimit, adminSetDailyLimit } from "../../lib/admin";
import { Loader2, TrendingUp, Users, CheckCircle2, IndianRupee, BookText, Sparkles, CalendarClock, Pencil } from "lucide-react";
import { toast } from "sonner";

function StatCard({ icon: Icon, label, value, sub, testId }) {
    return (
        <div className="rounded-[20px] bg-white p-5 shadow-soft ring-1 ring-charcoal/5" data-testid={testId}>
            <div className="flex items-center gap-2 text-gold">
                <Icon className="h-3.5 w-3.5" />
                <span className="text-[10px] uppercase tracking-[0.22em]">{label}</span>
            </div>
            <div className="mt-3 font-serif text-3xl text-charcoal">{value}</div>
            {sub && <div className="mt-1 text-[12px] text-ink">{sub}</div>}
        </div>
    );
}

function DailyLimitSection() {
    const [status, setStatus] = useState(null);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState("");
    const [saving, setSaving] = useState(false);

    const load = () => adminGetDailyLimit().then(setStatus).catch(() => {});
    useEffect(() => { load(); }, []);

    async function save() {
        const n = parseInt(draft, 10);
        if (!n || n < 1) { toast.error("Enter a limit of at least 1."); return; }
        setSaving(true);
        try {
            await adminSetDailyLimit(n);
            toast.success(`Daily limit updated to ${n}. Existing allocations today are untouched.`);
            setEditing(false);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to update daily limit.");
        } finally {
            setSaving(false);
        }
    }

    if (!status) {
        return (
            <div className="rounded-[24px] bg-white p-6 shadow-soft ring-1 ring-charcoal/5" data-testid="admin-daily-limit-loading">
                <Loader2 className="h-4 w-4 animate-spin text-gold" />
            </div>
        );
    }

    return (
        <div className="rounded-[24px] bg-white p-6 shadow-soft ring-1 ring-charcoal/5" data-testid="admin-daily-limit">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-gold">
                    <CalendarClock className="h-4 w-4" />
                    <h2 className="font-serif text-xl text-charcoal">Daily Paid-Report Limit</h2>
                </div>
                {!editing && (
                    <button
                        type="button"
                        onClick={() => { setDraft(String(status.limit)); setEditing(true); }}
                        data-testid="admin-daily-limit-edit-btn"
                        className="inline-flex items-center gap-1.5 rounded-full border border-charcoal/15 px-3 py-1.5 text-[11px] uppercase tracking-[0.16em] text-charcoal hover:border-charcoal"
                    >
                        <Pencil className="h-3 w-3" /> Change limit
                    </button>
                )}
            </div>

            {editing ? (
                <div className="mt-4 flex items-center gap-3">
                    <input
                        type="number"
                        min={1}
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        data-testid="admin-daily-limit-input"
                        className="w-28 rounded-lg border border-charcoal/20 px-3 py-2 text-sm"
                    />
                    <button type="button" onClick={save} disabled={saving} data-testid="admin-daily-limit-save-btn" className="btn-gold !py-2 !px-4 text-[12px]">
                        {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save"}
                    </button>
                    <button type="button" onClick={() => setEditing(false)} className="text-[12px] text-charcoal/60 hover:text-charcoal">
                        Cancel
                    </button>
                </div>
            ) : (
                <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-5" data-testid="admin-daily-limit-stats">
                    <div><div className="text-[10px] uppercase tracking-[0.16em] text-charcoal/50">IST Date</div><div className="mt-1 font-serif text-lg text-charcoal">{status.date_ist}</div></div>
                    <div><div className="text-[10px] uppercase tracking-[0.16em] text-charcoal/50">Configured Limit</div><div className="mt-1 font-serif text-lg text-charcoal">{status.limit}</div></div>
                    <div><div className="text-[10px] uppercase tracking-[0.16em] text-charcoal/50">Allocated Today</div><div className="mt-1 font-serif text-lg text-charcoal">{status.allocated}</div></div>
                    <div><div className="text-[10px] uppercase tracking-[0.16em] text-charcoal/50">Remaining</div><div className="mt-1 font-serif text-lg text-charcoal">{status.remaining}</div></div>
                    <div><div className="text-[10px] uppercase tracking-[0.16em] text-charcoal/50">Scheduled / Generating</div><div className="mt-1 font-serif text-lg text-charcoal">{status.scheduled_orders} / {status.generating_orders}</div></div>
                </div>
            )}
            {status.full && (
                <p className="mt-4 text-[12px] text-amber-700" data-testid="admin-daily-limit-full-notice">
                    Today's slots are filled — new paid orders are being queued for tomorrow (IST) automatically.
                </p>
            )}
        </div>
    );
}

function StatusPill({ status }) {
    const map = {
        ready: ["bg-green-100", "text-green-800"],
        generating: ["bg-amber-100", "text-amber-800"],
        partial: ["bg-amber-100", "text-amber-800"],
        unlocking: ["bg-amber-100", "text-amber-800"],
        failed: ["bg-rose-100", "text-rose-800"],
    };
    const [bg, fg] = map[status] || ["bg-charcoal/5", "text-charcoal/70"];
    return (
        <span className={`inline-flex items-center rounded-full ${bg} px-2 py-0.5 text-[10px] uppercase tracking-[0.16em] ${fg}`}>
            {status || "—"}
        </span>
    );
}

export default function AdminDashboard() {
    const [stats, setStats] = useState(null);
    const [analyses, setAnalyses] = useState([]);
    const [filter, setFilter] = useState("all"); // all | paid | free
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        Promise.all([
            adminStats(),
            adminListAnalyses({ limit: 25, ...(filter === "paid" ? { paid: true } : filter === "free" ? { paid: false } : {}) }),
        ])
            .then(([s, a]) => {
                if (!alive) return;
                setStats(s);
                setAnalyses(a.items);
            })
            .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load admin data."))
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [filter]);

    if (loading || !stats) {
        return (
            <div className="flex h-60 items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-gold" />
            </div>
        );
    }

    return (
        <div className="space-y-8" data-testid="admin-dashboard">
            <div>
                <h1 className="font-serif text-3xl text-charcoal">Dashboard</h1>
                <p className="text-[13.5px] text-ink">Live counters from the production database.</p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard icon={TrendingUp} label="Total analyses" value={stats.total_analyses} sub={`${stats.last_7_days_analyses} in the last 7 days`} testId="stat-total" />
                <StatCard icon={CheckCircle2} label="Paid couples" value={stats.paid_count} sub={`${stats.paid_conversion_pct}% conversion`} testId="stat-paid" />
                <StatCard icon={IndianRupee} label="Revenue" value={`₹${stats.revenue_rupees.toLocaleString("en-IN")}`} sub={stats.paid_count ? `avg ₹${Math.round(stats.revenue_rupees / stats.paid_count).toLocaleString("en-IN")} / couple` : "no paid customers yet"} testId="stat-revenue" />
                <StatCard icon={Users} label="Email leads" value={stats.leads} sub="captured via landing CTA" testId="stat-leads" />
                <StatCard icon={Sparkles} label="Galleries ready" value={stats.ready_galleries} sub="all 10 Nano Banana images rendered" testId="stat-galleries" />
                <StatCard icon={BookText} label="Story Studio forms" value={stats.story_studio_completed} sub="couples who finished the questionnaire" testId="stat-story" />
            </div>

            <DailyLimitSection />

            <div className="rounded-[24px] bg-white p-6 shadow-soft ring-1 ring-charcoal/5">
                <div className="flex items-center justify-between gap-4">
                    <h2 className="font-serif text-xl text-charcoal">Recent analyses</h2>
                    <div className="flex gap-1.5">
                        {["all", "paid", "free"].map((f) => (
                            <button
                                key={f}
                                type="button"
                                onClick={() => setFilter(f)}
                                data-testid={`admin-filter-${f}`}
                                className={`rounded-full px-3 py-1 text-[11px] uppercase tracking-[0.18em] transition ${
                                    filter === f
                                        ? "bg-charcoal text-ivory"
                                        : "text-charcoal/60 hover:text-charcoal"
                                }`}
                            >
                                {f}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="-mx-2 mt-5 overflow-x-auto">
                    <table className="w-full min-w-[800px] text-[13px]" data-testid="admin-analyses-table">
                        <thead>
                            <tr className="text-left text-[10px] uppercase tracking-[0.16em] text-charcoal/55">
                                <th className="px-2 py-2">Couple</th>
                                <th className="px-2 py-2">Occasion</th>
                                <th className="px-2 py-2">Created</th>
                                <th className="px-2 py-2">Report</th>
                                <th className="px-2 py-2">Gallery</th>
                                <th className="px-2 py-2">Paid</th>
                                <th className="px-2 py-2" />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-charcoal/5">
                            {analyses.map((a) => (
                                <tr key={a.id} className="hover:bg-champagne/30" data-testid={`row-${a.id}`}>
                                    <td className="px-2 py-3 font-medium text-charcoal">{a.name || "—"}</td>
                                    <td className="px-2 py-3 text-ink">{a.occasion || "—"}</td>
                                    <td className="px-2 py-3 text-ink">{a.created_at ? new Date(a.created_at).toLocaleDateString() : "—"}</td>
                                    <td className="px-2 py-3"><StatusPill status={a.report_status} /></td>
                                    <td className="px-2 py-3"><StatusPill status={a.gallery_status} /></td>
                                    <td className="px-2 py-3">
                                        {a.paid ? (
                                            <span className="inline-flex items-center rounded-full bg-gold/15 px-2 py-0.5 text-[10px] uppercase tracking-[0.16em] text-charcoal">
                                                Paid · ₹{Math.round((a.amount_paise || 0) / 100).toLocaleString("en-IN")}
                                            </span>
                                        ) : (
                                            <span className="text-[11px] text-charcoal/40">—</span>
                                        )}
                                    </td>
                                    <td className="px-2 py-3 text-right">
                                        <Link to={`/admin/analysis/${a.id}`} className="text-[11px] uppercase tracking-[0.18em] text-gold hover:underline" data-testid={`row-${a.id}-view`}>
                                            View →
                                        </Link>
                                    </td>
                                </tr>
                            ))}
                            {analyses.length === 0 && (
                                <tr><td colSpan={7} className="px-2 py-8 text-center text-[12.5px] text-charcoal/50">No analyses in this filter.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export function AdminLeads() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        adminListLeads({ limit: 200 })
            .then((d) => setItems(d.items))
            .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load leads."))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div className="space-y-6" data-testid="admin-leads">
            <h1 className="font-serif text-3xl text-charcoal">Leads</h1>
            <div className="rounded-[24px] bg-white p-6 shadow-soft ring-1 ring-charcoal/5">
                {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin text-gold" />
                ) : items.length === 0 ? (
                    <p className="text-[12.5px] text-charcoal/55">No leads captured yet.</p>
                ) : (
                    <table className="w-full text-[13px]">
                        <thead>
                            <tr className="text-left text-[10px] uppercase tracking-[0.16em] text-charcoal/55">
                                <th className="py-2">Email</th>
                                <th className="py-2">Source</th>
                                <th className="py-2">Captured</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-charcoal/5">
                            {items.map((l) => (
                                <tr key={l.id || l.email}>
                                    <td className="py-3 font-medium text-charcoal">{l.email}</td>
                                    <td className="py-3 text-ink">{l.source || "—"}</td>
                                    <td className="py-3 text-ink">{l.created_at ? new Date(l.created_at).toLocaleString() : "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
